// Card de uma oferta do feed: dados da API, gerar link oficial (convert-link),
// copiar e enviar para os destinos.

import { useState } from "react";
import { Check, Copy, Link2, Send, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { composeOfferText } from "@/lib/auto-promotions";
import { formatPriceCents } from "@/lib/marketplace";
import { useClipboard } from "@/lib/hooks/useClipboard";
import { callErrorMessage, convertLink, type FeedOffer, type FeedStore } from "@/lib/hooks/useConvertLink";
import { useForwardingSettings } from "@/lib/hooks/useForwarding";
import { useTextTemplates } from "@/lib/hooks/useTextTemplates";
import { SendOfferForm } from "./SendOfferForm";

interface OfferCardProps {
  readonly offer: FeedOffer;
  readonly store: FeedStore;
}

interface Generated {
  readonly url: string;
  readonly linkId: string | null;
}

function Metrics({ offer }: { offer: FeedOffer }): JSX.Element {
  const parts: string[] = [];
  if (offer.commissionPercent !== null) parts.push(`Comissão ${String(offer.commissionPercent).replace(".", ",")}%`);
  if (offer.sales !== null) parts.push(`${offer.sales.toLocaleString("pt-BR")} vendas`);
  return (
    <p className="flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
      {parts.join(" · ")}
      {offer.rating !== null && (
        <span className="inline-flex items-center gap-0.5">
          <Star size={11} className="fill-[hsl(var(--gold))] text-[hsl(var(--gold))]" aria-hidden="true" />
          {offer.rating.toFixed(1).replace(".", ",")}
        </span>
      )}
    </p>
  );
}

export function OfferCard({ offer, store }: OfferCardProps): JSX.Element {
  const [generated, setGenerated] = useState<Generated | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showSend, setShowSend] = useState(false);
  const { copy, copiedKey } = useClipboard();
  const { data: settings } = useForwardingSettings();
  const { data: templates = [] } = useTextTemplates();

  const generate = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const result = await convertLink(offer.productUrl, true);
      const url = result.finalUrl ?? result.affiliateUrl;
      if (result.status !== "converted" || !url) {
        setError(result.message ?? "Não foi possível gerar o link desta oferta.");
        return;
      }
      setGenerated({ url, linkId: result.linkId ?? null });
    } catch (err) {
      setError(callErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const text = generated && settings ? composeOfferText(offer, generated.url, store, settings, templates) : "";

  return (
    <Card className="flex h-full flex-col overflow-hidden">
      {offer.imageUrl ? (
        <img src={offer.imageUrl} alt="" loading="lazy" width={320} height={320} className="aspect-square w-full bg-[hsl(var(--surface-raised))] object-cover" />
      ) : (
        <div className="aspect-square w-full bg-[hsl(var(--surface-raised))]" aria-hidden="true" />
      )}
      <CardContent className="flex flex-1 flex-col gap-2 p-4">
        <h3 className="line-clamp-2 text-sm font-medium leading-snug text-foreground">{offer.title}</h3>
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="text-base font-semibold text-foreground">{formatPriceCents(offer.priceCents) ?? "Preço indisponível"}</span>
          {offer.originalPriceCents !== null && (
            <span className="text-xs text-muted-foreground line-through">{formatPriceCents(offer.originalPriceCents)}</span>
          )}
          {offer.discountPercent !== null && (
            <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[11px] font-semibold text-primary">-{offer.discountPercent}%</span>
          )}
        </div>
        <Metrics offer={offer} />
        <div className="mt-auto space-y-2 pt-2">
          {!generated ? (
            <Button size="sm" className="w-full" onClick={() => void generate()} disabled={busy}>
              <Link2 size={14} aria-hidden="true" /> {busy ? "Gerando..." : "Gerar link"}
            </Button>
          ) : (
            <>
              <code className="block truncate rounded bg-[hsl(var(--surface-raised))] px-2 py-1 font-mono text-[11px] text-muted-foreground">{generated.url}</code>
              <div className="flex gap-2">
                <Button size="sm" variant="secondary" className="flex-1" onClick={() => void copy(text || generated.url, offer.itemId)}>
                  {copiedKey === offer.itemId ? <Check size={14} aria-hidden="true" /> : <Copy size={14} aria-hidden="true" />}
                  {copiedKey === offer.itemId ? "Copiado" : "Copiar post"}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setShowSend((v) => !v)} aria-expanded={showSend}>
                  <Send size={14} aria-hidden="true" /> Enviar
                </Button>
              </div>
              {showSend && (
                <SendOfferForm
                  text={text || generated.url}
                  imageUrl={offer.imageUrl}
                  linkId={generated.linkId}
                  link={{ title: offer.title, priceCents: offer.priceCents, originalPriceCents: offer.originalPriceCents, coupon: null, url: generated.url, marketplaceCode: store }}
                />
              )}
            </>
          )}
          {error && (
            <p role="alert" className="text-xs text-destructive">
              {error}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
